/** @jsxImportSource theme-ui */
import { Box, Text, Heading, Image } from "theme-ui";
import PropTypes from "prop-types";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { typography, colors } from "../../styles/theme";
import { Button } from "../atoms";

const InfoSection = ({
  styledBackground,
  image,
  imageAltText,
  imageUrl,
  videoUrl,
  mediaPosition,
  label,
  title,
  description,
  primaryButtonLabel,
  secondaryButtonLabel,
  onPrimaryBtnClick,
  onSecondaryBtnClick,
  primaryBtnVariant,
  primaryBtnWidth,
  secondaryBtnVariant,
  secondaryBtnWidth,
  customStyles
}) => {
  const {
    container,
    styledBackgroundStyles,
    imageContainer,
    videoContainer,
    imageStyle,
    infoContainer,
    content,
    labelStyle,
    titleStyle,
    bodyTextStyle,
    buttonContainer,
  } = infoSectionStyles;
  return (
    <Box
      sx={{
        ...container,
        ...(styledBackground ? styledBackgroundStyles : {}),
        ...customStyles,
      }}
    >
      {image && mediaPosition === "left" && (
        <GatsbyImage
          image={getImage(image)}
          sx={imageContainer}
          imgStyle={imageStyle}
          alt={imageAltText}
        />
      )}
      {imageUrl && mediaPosition === "left" && (
        <Image
          src={imageUrl}
          alt={imageAltText}
          sx={{
            ...imageContainer,
            ...imageStyle,
          }}
        />
      )}
      {videoUrl && (
        <Box sx={videoContainer}>
          <video controls className="info-video">
            <source src={videoUrl} type="video/mp4"/>
          </video>
        </Box>
      )}
      <Box sx={infoContainer}>
        <Box sx={content}>
          <Text sx={labelStyle}>{label}</Text>
          <Heading as="h2" sx={titleStyle}>
            {title}
          </Heading>
          <Text sx={bodyTextStyle}>{description}</Text>
        </Box>
        <Box sx={buttonContainer}>
          {primaryButtonLabel && (
            <Button
              size="large"
              variant={primaryBtnVariant}
              btnWidth={primaryBtnWidth}
              onClick={onPrimaryBtnClick}
            >
              {primaryButtonLabel}
            </Button>
          )}
          {secondaryButtonLabel && (
            <Button
              size="large"
              variant={secondaryBtnVariant}
              btnWidth={secondaryBtnWidth}
              onClick={onSecondaryBtnClick}
            >
              {secondaryButtonLabel}
            </Button>
          )}
        </Box>
      </Box>
      {image && mediaPosition === "right" && (
        <GatsbyImage
          image={getImage(image)}
          sx={imageContainer}
          imgStyle={imageStyle}
          alt={image.alt}
        />
      )}
      {imageUrl && mediaPosition === "right" && (
        <Image
          src={imageUrl}
          alt={imageAltText}
          sx={{
            ...imageContainer,
            ...imageStyle,
          }}
        />
      )}
    </Box>
  );
};

const infoSectionStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: ["1.5rem", "1.5rem", "1.5rem", "4rem"],
    flexDirection: ["column", "column", "row", "row"],
  },
  styledBackgroundStyles: {
    padding: ["1.5rem", "1.5rem", "1.5rem", "4.5rem 8.9%"],
    backgroundColor: colors.primary100,
    borderRadius: "20px",
  },
  videoContainer: {
    width: "100%",
    ".info-video": {
      width: "100%",
      borderRadius: "20px",
    },
  },
  imageContainer: {
    width: "100%",
    height: "19rem",
  },
  imageStyle: {
    borderRadius: "20px",
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  labelStyle: {
    ...typography.textSm,
    color: colors.primary800,
    mb: "0.375rem",
  },
  titleStyle: {
    ...typography.h3,
    color: colors.black,
    mb: "0.375rem",
  },
  bodyTextStyle: {
    ...typography.textMd400,
    color: colors.black,
  },
  buttonContainer: {
    display: "flex",
    mt: "1.563rem",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "1.125rem",
  },
};

InfoSection.propTypes = {
  styledBackground: PropTypes.bool,
  mediaPosition: PropTypes.oneOf(["left", "right"]),
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  image: PropTypes.object,
  imageAltText: PropTypes.string,
  imageUrl: PropTypes.string,
  videoUrl: PropTypes.string,
  primaryButtonLabel: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  onPrimaryBtnClick: PropTypes.func,
  onSecondaryBtnClick: PropTypes.func,
  customStyles: PropTypes.object,
  primaryBtnVariant: PropTypes.oneOf(["filled", "outlined"]),
  primaryBtnWidth: PropTypes.oneOf(["default", "full"]),
  secondaryBtnVariant: PropTypes.oneOf(["filled", "outlined"]),
  secondaryBtnWidth: PropTypes.oneOf(["default", "full"]),
};

InfoSection.defaultProps = {
  mediaPosition: "left",
  imageAltText: "",
  imageUrl: "",
  label: "",
  title: "",
  description: "",
  primaryButtonLabel: "",
  secondaryButtonLabel: "",
  onPrimaryBtnClick: () => {},
  onSecondaryBtnClick: () => {},
  customStyles: {},
};

export default InfoSection;
